import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import GoogleCalendarCard from '../components/GoogleCalendar';
import { formatDate, formatEuro, clienteNome, statoLabel } from '../components/shared';

// ─── PAGINE DI RIFERIMENTO (titoli delle sezioni linkabili) ──────────────────
// Se la pagina "Piani di rateazione" ha un percorso diverso in App.jsx,
// cambia solo questa riga.
const LINK_SCADENZE    = '/scadenze';
const LINK_ATTIVITA    = '/attivita';
const LINK_RATEAZIONI  = '/rateazioni';

const RIGHE_INIZIALI = 10;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
async function getJson(url) {
  let token = '';
  try { token = localStorage.getItem('sp_token') || ''; } catch (e) { /* storage non disponibile */ }
  const res = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
  const j = await res.json();
  if (!j.success) throw new Error(j.error || 'Errore di caricamento');
  return j.data;
}

// Giorni da oggi (negativo = scaduta). Confronto su date pure, senza orari.
function giorniA(data) {
  if (!data) return null;
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  const d = new Date(String(data).slice(0, 10) + 'T00:00:00');
  if (isNaN(d)) return null;
  return Math.round((d - oggi) / 86400000);
}

function toneDa(diff) {
  if (diff === null) return 'var(--text-faint)';
  if (diff <= 7)     return 'var(--c-red)';
  if (diff <= 30)    return 'var(--c-amber)';
  return 'var(--text-muted)';
}

function etichettaGiorni(diff) {
  if (diff === null) return '';
  if (diff < 0)      return `${Math.abs(diff)}gg fa`;
  if (diff === 0)    return 'oggi';
  return `tra ${diff}gg`;
}

// ─── CARD CON TITOLO LINKABILE ────────────────────────────────────────────────
function Sezione({ titolo, to, conteggio, extra, children, style }) {
  return (
    <div className="card" style={style}>
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to={to} className="card-title" title={`Vai a ${titolo}`}
            style={{ textDecoration: 'none', color: 'inherit' }}>
            {titolo} <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>›</span>
          </Link>
          {conteggio !== undefined && conteggio !== null && (
            <span className="pill pill-gray" style={{ fontSize: 11 }}>{conteggio}</span>
          )}
          {extra}
        </div>
      </div>
      {children}
    </div>
  );
}

function Vuoto({ testo }) {
  return <div style={{ textAlign: 'center', color: 'var(--text-faint)', padding: '28px 0', fontSize: 13 }}>{testo}</div>;
}

function Errore({ testo }) {
  return <div style={{ textAlign: 'center', color: 'var(--c-red)', padding: '28px 0', fontSize: 13 }}>⚠ {testo}</div>;
}

function MostraAltre({ totale, tutte, onToggle }) {
  if (totale <= RIGHE_INIZIALI) return null;
  return (
    <div style={{ padding: '10px 18px', borderTop: '1px solid var(--row-line)', textAlign: 'center' }}>
      <button className="btn btn-sm" onClick={onToggle}>
        {tutte ? '▲ Mostra meno' : `▼ Mostra tutte (${totale})`}
      </button>
    </div>
  );
}

// ─── SCADENZE (cronologico, per scadenza e non per singolo cliente) ───────────
function ScadenzeCard({ navigate }) {
  const [gruppi, setGruppi]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore]   = useState('');
  const [tutte, setTutte]     = useState(false);

  useEffect(() => {
    api.getScadenzeRaggruppate({})
      .then(g => setGruppi(Array.isArray(g) ? g : []))
      .catch(e => setErrore(e.message || 'Errore di caricamento'))
      .finally(() => setLoading(false));
  }, []);

  // Solo scadenze con almeno una posizione ancora da lavorare (aperta o preparata),
  // in ordine di data crescente: prima le scadute, poi le prossime.
  const lista = useMemo(() =>
    gruppi
      .filter(g => (g.n_aperte || 0) + (g.n_preparata || 0) > 0)
      .sort((a, b) => (a.data_scad || '9999').localeCompare(b.data_scad || '9999')),
  [gruppi]);

  const visibili = tutte ? lista : lista.slice(0, RIGHE_INIZIALI);
  const scadute  = lista.filter(g => { const d = giorniA(g.data_scad); return d !== null && d < 0; }).length;

  return (
    <Sezione titolo="Scadenze" to={LINK_SCADENZE} conteggio={lista.length}
      extra={scadute > 0 && <span className="pill pill-red" style={{ fontSize: 11 }}>{scadute} scadute</span>}>
      {loading ? <Vuoto testo="Caricamento…" />
      : errore ? <Errore testo={errore} />
      : lista.length === 0 ? <Vuoto testo="Nessuna scadenza da lavorare" />
      : (
        <div>
          {visibili.map((g, i) => {
            const diff   = giorniA(g.data_scad);
            const colore = toneDa(diff);
            const tot    = g.n_clienti || 0;
            const fatte  = tot - (g.n_aperte || 0);
            const pct    = tot > 0 ? Math.round((fatte / tot) * 100) : 0;
            return (
              <div key={`${g.tipo}-${g.descrizione}-${g.data_scad}-${i}`}
                onClick={() => navigate(LINK_SCADENZE)}
                style={{ display: 'grid', gridTemplateColumns: '84px 1fr 96px', gap: 12, alignItems: 'center',
                  padding: '10px 18px', borderBottom: '1px solid var(--row-line)', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: colore }}>{formatDate(g.data_scad)}</div>
                  <div style={{ fontSize: 10.5, color: colore }}>{etichettaGiorni(diff)}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {g.descrizione || g.tipo}
                  </div>
                  <div style={{ marginTop: 3 }}><span className="pill pill-gray" style={{ fontSize: 10.5 }}>{g.tipo}</span></div>
                </div>
                <div>
                  <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%',
                      background: pct === 100 ? 'var(--c-green)' : pct > 50 ? 'var(--c-amber)' : 'var(--accent)' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 3, textAlign: 'right' }}>
                    {fatte}/{tot} clienti
                  </div>
                </div>
              </div>
            );
          })}
          <MostraAltre totale={lista.length} tutte={tutte} onToggle={() => setTutte(t => !t)} />
        </div>
      )}
    </Sezione>
  );
}

// ─── ATTIVITÀ APERTE ──────────────────────────────────────────────────────────
const PILL_ATTIVITA = { APERTA: 'pill-blue', IN_CORSO: 'pill-amber' };

function AttivitaCard({ navigate }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore]   = useState('');
  const [tutte, setTutte]     = useState(false);

  useEffect(() => {
    getJson('/api/attivita')
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(e => setErrore(e.message || 'Errore di caricamento'))
      .finally(() => setLoading(false));
  }, []);

  // Aperte = non completate (le archiviate il server non le restituisce).
  // Prima quelle con scadenza (la più vicina in alto), poi quelle senza data.
  const lista = useMemo(() =>
    rows
      .filter(a => a.stato !== 'COMPLETATA')
      .sort((a, b) => {
        const da = a.data_scad || '9999', db = b.data_scad || '9999';
        return da.localeCompare(db) || String(b.updated_at || '').localeCompare(String(a.updated_at || ''));
      }),
  [rows]);

  const visibili = tutte ? lista : lista.slice(0, RIGHE_INIZIALI);

  return (
    <Sezione titolo="Attività aperte" to={LINK_ATTIVITA} conteggio={lista.length}>
      {loading ? <Vuoto testo="Caricamento…" />
      : errore ? <Errore testo={errore} />
      : lista.length === 0 ? <Vuoto testo="Nessuna attività aperta" />
      : (
        <div>
          {visibili.map(a => {
            const diff   = giorniA(a.data_scad);
            const colore = toneDa(diff);
            return (
              <div key={a.id} onClick={() => navigate(LINK_ATTIVITA)}
                style={{ padding: '10px 18px', borderBottom: '1px solid var(--row-line)', cursor: 'pointer',
                  display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.titolo}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                    {clienteNome(a)}{a.assegnata_a ? ` · ${a.assegnata_a}` : ''}
                    {a.perc_compl > 0 ? ` · ${a.perc_compl}%` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span className={`pill ${PILL_ATTIVITA[a.stato] || 'pill-gray'}`} style={{ fontSize: 10.5 }}>
                    {statoLabel(a.stato)}
                  </span>
                  <div style={{ fontSize: 11, marginTop: 3, color: colore }}>
                    {a.data_scad ? `${formatDate(a.data_scad)} · ${etichettaGiorni(diff)}` : 'senza scadenza'}
                  </div>
                </div>
              </div>
            );
          })}
          <MostraAltre totale={lista.length} tutte={tutte} onToggle={() => setTutte(t => !t)} />
        </div>
      )}
    </Sezione>
  );
}

// ─── RATE AVVISI / CARTELLE ───────────────────────────────────────────────────
const STATO_RATA_LABEL = { NON_PAGATA: 'Non pagata', SCADUTA: 'Scaduta', SOSPESA: 'Sospesa', PAGATA_IN_RITARDO: 'In ritardo' };
const STATO_RATA_PILL  = { NON_PAGATA: 'pill-red', SCADUTA: 'pill-red', SOSPESA: 'pill-gray', PAGATA_IN_RITARDO: 'pill-amber' };

function nomeCliente(r) {
  return r.denominazione || r.ragione_sociale || [r.nome, r.cognome].filter(Boolean).join(' ') || '—';
}

function RateCard({ navigate }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore]   = useState('');
  const [tutte, setTutte]     = useState(false);

  const carica = useCallback(() => {
    getJson('/api/avvisi_rate/dashboard')
      .then(r => setRows(Array.isArray(r) ? r : []))
      .catch(e => setErrore(e.message || 'Errore di caricamento'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const visibili = tutte ? rows : rows.slice(0, RIGHE_INIZIALI);
  const scadute  = rows.filter(r => r.stato === 'SCADUTA').length;

  return (
    <Sezione titolo="Rate avvisi e cartelle" to={LINK_RATEAZIONI} conteggio={rows.length}
      extra={scadute > 0 && <span className="pill pill-red" style={{ fontSize: 11 }}>{scadute} scadute</span>}
      style={{ marginTop: 16 }}>
      {loading ? <Vuoto testo="Caricamento…" />
      : errore ? <Errore testo={errore} />
      : rows.length === 0 ? <Vuoto testo="Nessuna rata in scadenza nei prossimi 6 mesi" />
      : (
        <div>
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Cliente</th><th>Avviso</th><th>Rata</th><th>Scadenza</th><th>Importo</th><th>Stato</th>
              </tr></thead>
              <tbody>
                {visibili.map(r => {
                  const diff   = giorniA(r.data_scad);
                  const colore = r.stato === 'SCADUTA' ? 'var(--c-red)' : toneDa(diff);
                  return (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/avvisi/${r.id_avviso}`)}>
                      <td className="fw-500">{nomeCliente(r)}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.tipo}{r.numero ? ` n.${r.numero}` : ''}</td>
                      <td style={{ fontWeight: 500 }}>#{r.rata}</td>
                      <td style={{ fontSize: 12, color: colore, fontWeight: diff !== null && diff <= 7 ? 600 : 400 }}>
                        {formatDate(r.data_scad)}
                        <span style={{ fontSize: 10, marginLeft: 4 }}>({etichettaGiorni(diff)})</span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {r.importo_totale || r.importo_totale === 0 ? formatEuro(r.importo_totale) : '—'}
                      </td>
                      <td>
                        <span className={`pill ${STATO_RATA_PILL[r.stato] || 'pill-gray'}`} style={{ fontSize: 11 }}>
                          {STATO_RATA_LABEL[r.stato] || r.stato}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <MostraAltre totale={rows.length} tutte={tutte} onToggle={() => setTutte(t => !t)} />
        </div>
      )}
    </Sezione>
  );
}

// ─── MENU ONEDRIVE (Clienti + anno corrente + due precedenti) ─────────────────
const iconaFile = (nome) => {
  const e = String(nome).split('.').pop().toLowerCase();
  if (e === 'pdf') return '📕';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(e)) return '🖼️';
  if (['doc', 'docx', 'odt', 'rtf'].includes(e)) return '📝';
  if (['xls', 'xlsx', 'ods', 'csv'].includes(e)) return '📊';
  if (['zip', 'rar', '7z'].includes(e)) return '🗜️';
  if (['eml', 'msg'].includes(e)) return '✉️';
  return '📎';
};
const dimensioneFile = (n) => {
  if (n == null || n < 0) return '';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return Math.round(n / 1024) + ' KB';
  return (n / 1048576).toFixed(1).replace('.', ',') + ' MB';
};
const dataBreve = (s) => { try { return s ? new Date(s).toLocaleDateString('it-IT') : ''; } catch (e) { return ''; } };

function OneDriveCard() {
  const [menu, setMenu]       = useState(null);     // { abilitato, voci:[{radice,etichetta,presente}] }
  const [radice, setRadice]   = useState(null);
  const [sub, setSub]         = useState('');
  const [voci, setVoci]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore]   = useState('');
  const [cerca, setCerca]     = useState('');

  useEffect(() => {
    getJson('/api/onedrive-menu')
      .then(setMenu)
      .catch(() => setMenu({ abilitato: false, voci: [] }));
  }, []);

  useEffect(() => {
    if (!radice) return;
    let attivo = true;
    setLoading(true); setErrore(''); setCerca('');
    getJson(`/api/onedrive-menu/elenco?radice=${encodeURIComponent(radice)}&sub=${encodeURIComponent(sub)}`)
      .then(d => { if (attivo) setVoci(Array.isArray(d.voci) ? d.voci : []); })
      .catch(e => { if (attivo) { setVoci([]); setErrore(e.message || 'Errore di caricamento'); } })
      .finally(() => { if (attivo) setLoading(false); });
    return () => { attivo = false; };
  }, [radice, sub]);

  if (!menu || !menu.abilitato) return null;

  const scegli = (r) => {
    if (r === radice && sub === '') { setRadice(null); return; }   // secondo clic: chiude
    setRadice(r); setSub('');
  };
  const entra = (nome) => setSub(sub ? sub + '/' + nome : nome);
  const livelli = sub ? sub.split('/') : [];
  const vaiA = (n) => setSub(livelli.slice(0, n).join('/'));

  const apri = async (v) => {
    const w = window.open('', '_blank');
    try {
      const r = await api.post('/api/onedrive-menu/link', { radice, sub, nome: v.nome });
      if (w) w.location.href = r.url; else window.location.href = r.url;
    } catch (e) { if (w) w.close(); setErrore(e.message || 'Impossibile aprire il file'); }
  };

  const etichetta = (menu.voci.find(x => x.radice === radice) || {}).etichetta || radice;
  const filtro = cerca.trim().toLowerCase();
  const mostrate = filtro ? voci.filter(v => v.nome.toLowerCase().includes(filtro)) : voci;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
        <span className="card-title">☁ OneDrive</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {menu.voci.map(v => (
            <button key={v.radice}
              className={'btn btn-sm' + (radice === v.radice ? ' btn-primary' : '')}
              disabled={v.presente === false}
              title={v.presente === false ? 'Cartella non presente su OneDrive' : ''}
              onClick={() => scegli(v.radice)}>
              {v.etichetta === 'Clienti' ? '👥 ' : '📅 '}{v.etichetta}
            </button>
          ))}
        </div>
      </div>

      {menu.errore && !radice && (
        <div style={{ padding: '10px 18px', fontSize: 12, color: 'var(--c-amber)' }}>⚠ {menu.errore}</div>
      )}

      {radice && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 18px', borderBottom: '1px solid var(--row-line)', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12.5, flex: 1, minWidth: 180 }}>
              <span style={{ cursor: livelli.length ? 'pointer' : 'default', color: livelli.length ? 'var(--accent)' : 'inherit', fontWeight: 600 }}
                onClick={() => livelli.length && vaiA(0)}>{etichetta}</span>
              {livelli.map((l, i) => (
                <span key={i}>
                  <span style={{ color: 'var(--text-faint)' }}> › </span>
                  <span style={{ cursor: i < livelli.length - 1 ? 'pointer' : 'default',
                    color: i < livelli.length - 1 ? 'var(--accent)' : 'inherit', fontWeight: i === livelli.length - 1 ? 600 : 400 }}
                    onClick={() => i < livelli.length - 1 && vaiA(i + 1)}>{l}</span>
                </span>
              ))}
            </div>
            <input className="form-control" placeholder="Cerca in questa cartella…" value={cerca}
              onChange={e => setCerca(e.target.value)} style={{ width: 220, padding: '5px 10px', fontSize: 12.5 }} />
          </div>

          {loading ? <Vuoto testo="Caricamento…" />
          : errore ? <Errore testo={errore} />
          : mostrate.length === 0 ? <Vuoto testo={filtro ? 'Nessun risultato' : 'Cartella vuota'} />
          : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {livelli.length > 0 && !filtro && (
                <div className="riga-elenco" onClick={() => vaiA(livelli.length - 1)}
                  style={{ padding: '8px 18px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>↩ Cartella superiore</div>
              )}
              {mostrate.map(v => (
                <div key={v.nome} className="riga-elenco"
                  onClick={() => (v.cartella ? entra(v.nome) : apri(v))}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px', cursor: 'pointer' }}>
                  <span style={{ width: 22, textAlign: 'center' }}>{v.cartella ? '📁' : iconaFile(v.nome)}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: v.cartella ? 500 : 400,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.nome}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-faint)', width: 70, textAlign: 'right' }}>{v.cartella ? '' : dimensioneFile(v.dimensione)}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-faint)', width: 80, textAlign: 'right' }}>{dataBreve(v.modificato)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();

  return (
    <div className="page-content">
      <GoogleCalendarCard />

      <OneDriveCard />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16, marginTop: 16 }}>
        <ScadenzeCard navigate={navigate} />
        <AttivitaCard navigate={navigate} />
      </div>

      <RateCard navigate={navigate} />
    </div>
  );
}
